/**
 * Callout "Modelo de cobranca: a definir pelo dono", com a SIMULACAO de como
 * os status deste recorte ficariam em cada opcao.
 *
 * Decisoes nao obvias:
 *  1. Tom `warning` de proposito: e decisao de produto PENDENTE (24/09/2026),
 *     nao erro. A regra da comissao (ADR-001) ja vale; falta decidir como
 *     cobrar.
 *  2. Tres colunas com os MESMOS numeros do recorte redistribuidos:
 *      - Contrato mensal: e como o extrato mostra hoje (a faturar no mes
 *        corrente, faturada, paga; revertida abatida na proxima fatura).
 *      - Automatico: a comissao de venda por webhook e retida na hora (split
 *        no provedor); venda ASSISTIDA nao passa por provedor e vira fatura
 *        avulsa -- consequencia que so aparece quando se poe os numeros lado a
 *        lado; revertida e devolvida pelo provedor no estorno.
 *      - A definir: tudo acumula, ninguem e cobrado.
 *  3. Nenhum seletor de modelo por empresa aqui: com centenas de empresas,
 *     uma lista de selects nao escala, e o lugar de alterar ja existe
 *     (Empresas -> Comissao e cobrança, com `updateCompany`). A tela mostra
 *     quantas empresas estao em cada modelo no cadastro vigente e leva ate la.
 */

import { Badge, Footnote, Icon, LinkButton, Panel } from '../../../components/ui';
import { formatBRLFromCents, formatCount } from '../../../lib/format';
import type { BillingModel, CompanySummary } from '../../../types';
import type { CommissionSummary } from '../types';
import { MODELO_COBRANCA, MODELOS_COBRANCA, formatMes, plural } from '../vocabulario';

interface LinhaDeStatus {
  readonly rotulo: string;
  readonly cents: number;
  readonly count: number;
  readonly tom: 'neutral' | 'good' | 'warning' | 'critical' | 'info' | 'accent';
}

const COR_DO_PONTO: Readonly<Record<LinhaDeStatus['tom'], string>> = {
  neutral: 'bg-ink-subtle',
  good: 'bg-status-good',
  warning: 'bg-status-warning',
  critical: 'bg-status-critical',
  info: 'bg-info',
  accent: 'bg-accent',
};

function linhasDo(modelo: BillingModel, s: CommissionSummary, mesAtual: string): readonly LinhaDeStatus[] {
  const revertida = s.byStatus.revertida;
  switch (modelo) {
    case 'contrato':
      return [
        { rotulo: `A faturar (fatura de ${formatMes(mesAtual)})`, cents: s.byStatus.a_faturar.commissionCents, count: s.byStatus.a_faturar.count, tom: 'warning' },
        { rotulo: 'Faturada, aguardando pagamento', cents: s.byStatus.faturada.commissionCents, count: s.byStatus.faturada.count, tom: 'info' },
        { rotulo: 'Paga', cents: s.byStatus.paga.commissionCents, count: s.byStatus.paga.count, tom: 'good' },
        { rotulo: 'Revertida: abatida na próxima fatura', cents: revertida.commissionCents, count: revertida.count, tom: 'critical' },
      ];
    case 'automatico':
      return [
        { rotulo: 'Retida na venda (split no provedor)', cents: s.activeBySource.webhook.commissionCents, count: s.activeBySource.webhook.count, tom: 'good' },
        { rotulo: 'Venda assistida: sem split, vira fatura avulsa', cents: s.activeBySource.manual.commissionCents, count: s.activeBySource.manual.count, tom: 'warning' },
        { rotulo: 'Revertida: o provedor devolve no estorno', cents: revertida.commissionCents, count: revertida.count, tom: 'critical' },
      ];
    case 'a_definir':
      return [
        { rotulo: 'Acumulada, sem cobrança', cents: s.commissionCents, count: s.conversionCount, tom: 'warning' },
        { rotulo: 'Revertida: sai da apuração', cents: revertida.commissionCents, count: revertida.count, tom: 'critical' },
      ];
  }
}

export function ModeloDeCobranca({
  summary,
  empresas,
  mesAtual,
  className = '',
}: {
  readonly summary: CommissionSummary;
  /** Empresas do recorte com o cadastro vigente (useCompany). */
  readonly empresas: readonly CompanySummary[];
  /** YYYY-MM do mes corrente. */
  readonly mesAtual: string;
  readonly className?: string;
}): JSX.Element {
  const contar = (modelo: BillingModel): number => empresas.filter((e) => e.billingModel === modelo).length;

  return (
    <Panel tone="warning" className={`flex flex-col gap-5 px-5 py-5 ${className}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span aria-hidden="true" className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-status-warning/15 text-status-warning">
            <Icon name="alerta" size="sm" />
          </span>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-ink">Modelo de cobrança: a definir pelo dono</h2>
            <p className="mt-0.5 max-w-3xl text-sm text-ink-muted">
              A regra da comissão (ADR-001) já vale; falta decidir <strong className="font-semibold text-ink">como</strong> cobrar. Abaixo, os mesmos{' '}
              {formatBRLFromCents(summary.commissionCents + summary.byStatus.revertida.commissionCents)} deste recorte redistribuídos em cada opção.
            </p>
          </div>
        </div>
        <LinkButton to="/admin/empresas" variant="secondary" size="sm" icon="empresa">
          Alterar por empresa
        </LinkButton>
      </div>

      <div className="grid gap-3 lg:grid-cols-3">
        {MODELOS_COBRANCA.map((modelo) => {
          const info = MODELO_COBRANCA[modelo];
          const usando = contar(modelo);
          return (
            <section key={modelo} aria-labelledby={`modelo-${modelo}`} className="flex flex-col gap-3 rounded-xl border border-line bg-surface px-4 py-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 id={`modelo-${modelo}`}>
                  <Badge tone={info.tone}>{info.label}</Badge>
                </h3>
                {modelo === 'contrato' ? <span className="text-[11px] font-medium text-ink-subtle">É como o extrato mostra hoje</span> : null}
              </div>
              <p className="text-xs text-ink-muted">{info.explicacao}</p>
              <ul className="flex flex-col gap-1.5">
                {linhasDo(modelo, summary, mesAtual).map((l) => (
                  <li key={l.rotulo} className="flex items-start justify-between gap-3 text-sm">
                    <span className="flex min-w-0 items-start gap-2 text-ink-muted">
                      <span aria-hidden="true" className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${COR_DO_PONTO[l.tom]}`} />
                      <span>
                        {l.rotulo} <span className="text-ink-subtle">· {formatCount(l.count)}</span>
                      </span>
                    </span>
                    <span className="shrink-0 font-medium tabular-nums text-ink">{formatBRLFromCents(l.cents)}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-auto border-t border-line pt-2 text-[11px] text-ink-subtle">
                {usando === 0 ? 'Nenhuma empresa do recorte neste modelo no cadastro.' : `${formatCount(usando)} ${plural(usando, 'empresa', 'empresas')} do recorte neste modelo no cadastro.`}
              </p>
            </section>
          );
        })}
      </div>

      <Footnote>Nada aqui cobra ninguém. A simulação usa só as conversões deste recorte; a escolha vale quando o dono decidir e registrar no ADR-001.</Footnote>
    </Panel>
  );
}
